import React from 'react';
import clsx from 'clsx';
import { Form } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import * as Headless from '@headlessui/react';
import { ArrowRightIcon, CheckIcon, SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { PaperAirplaneIcon, SparklesIcon as SolidSparklesIcon } from '@heroicons/react/24/solid';
import { Spinner } from '@oysterjs/ui/spinner';
import { AnimateIn } from '@oysterjs/ui/transition';

import type {
  CaraWorkspaceFieldsFragment,
  CaraWorkspaceMessageFollowUpInput,
  CaraWorkspaceMessageFollowUpOption
} from '@oysterjs/ais/api/graphql';

// The server caps a generation at a minute and then records the outcome either way,
// so a start with no completion past this point means the worker died mid-flight.
// Nothing retries a message that already carries a follow-up record, so without a
// cutoff the spinner outlives the page.
const STALE_GENERATION_MS = 2 * 60 * 1000;

// useGenerationIsStale reports a generation that will never complete, re-rendering
// once at the cutoff so a spinner started in this session also gives up.
const useGenerationIsStale = (startedAt: Date | null | undefined) => {
  const deadline = startedAt ? new Date(startedAt).getTime() + STALE_GENERATION_MS : null;
  const [isStale, setIsStale] = React.useState(() => deadline !== null && Date.now() > deadline);

  React.useEffect(() => {
    if (deadline === null) {
      setIsStale(false);
      return;
    }

    const remaining = deadline - Date.now();
    if (remaining <= 0) {
      setIsStale(true);
      return;
    }

    setIsStale(false);
    const timer = setTimeout(() => setIsStale(true), remaining);
    return () => clearTimeout(timer);
  }, [deadline]);

  return isStale;
};

export const ResponseFollowUpOptions = (props: {
  message: CaraWorkspaceFieldsFragment['messages'][number];
  onSelectFollowUp: (followUp: CaraWorkspaceMessageFollowUpInput) => void;
  usedFollowUpIds: Set<string>;
}) => {
  const [selectedFollowUpOption, setSelectedFollowUpOption] =
    React.useState<CaraWorkspaceMessageFollowUpOption | null>(null);
  const [editedPrompt, setEditedPrompt] = React.useState<string>('');
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const selectedOptionRef = React.useRef<HTMLDivElement>(null);
  const [hasAnimatedButtons, setHasAnimatedButtons] = React.useState(false);

  React.useEffect(() => {
    if (selectedFollowUpOption) {
      setEditedPrompt(selectedFollowUpOption.prompt);
      setTimeout(() => {
        if (selectedOptionRef.current) {
          const top = selectedOptionRef.current.getBoundingClientRect().top + window.scrollY;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }, 50);
    }
  }, [selectedFollowUpOption]);

  const triggerTextAreaResize = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  React.useEffect(() => {
    triggerTextAreaResize();
  }, [editedPrompt]);

  React.useEffect(() => {
    if (props.message.followUpOptions?.options?.length) {
      setHasAnimatedButtons(true);
    }
  }, [props.message.followUpOptions?.options?.length]);

  React.useEffect(() => {
    if (selectedFollowUpOption && props.usedFollowUpIds?.has(selectedFollowUpOption.id)) {
      setSelectedFollowUpOption(null);
    }
  }, [props.usedFollowUpIds]);

  const isGenerating =
    Boolean(props.message.followUpOptions?.generationStartedAt) &&
    !props.message.followUpOptions?.generationCompletedAt;
  const generationIsStale = useGenerationIsStale(
    isGenerating ? props.message.followUpOptions?.generationStartedAt : null
  );

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!selectedFollowUpOption) {
      return;
    }

    props.onSelectFollowUp?.({
      id: selectedFollowUpOption.id,
      prompt: editedPrompt
    });
  };

  if (!props.message.followUpOptions?.generationStartedAt || generationIsStale) {
    return null;
  }

  return (
    <AnimateIn>
      <div className={clsx('pt-4')} ref={selectedOptionRef}>
        {isGenerating && (
          <div className="flex items-center gap-2">
            <Spinner size="size-4" color="neutral" />
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              Generating follow-up options
            </p>
          </div>
        )}
        {props.message.followUpOptions?.generationCompletedAt &&
          (props.message.followUpOptions?.options?.length ?? 0) > 0 && (
            <>
              <AnimatePresence>
                {selectedFollowUpOption && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -10 }}
                    transition={{ duration: 0.2, ease: 'anticipate' }}
                    className="mb-4"
                  >
                    <div className="bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-2xl overflow-hidden shadow-sm">
                      <div className="flex items-start gap-3">
                        <div className="flex-1">
                          <div className="flex items-center justify-between bg-neutral-100 dark:bg-neutral-700 border-b border-neutral-100 dark:border-neutral-700 py-4 px-4">
                            <div className="flex items-center gap-2">
                              <SolidSparklesIcon className="size-5 text-neutral-600 dark:text-neutral-400 flex-shrink-0" />
                              <h4 className="text-sm font-medium font-body text-neutral-900 dark:text-white m-0 p-0">
                                {selectedFollowUpOption.title}
                              </h4>
                            </div>
                            <Headless.Button
                              onClick={() => setSelectedFollowUpOption(null)}
                              className="p-1 text-neutral-600 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-white/10 rounded transition-colors"
                            >
                              <XMarkIcon className="size-4" />
                            </Headless.Button>
                          </div>
                          <Form onSubmit={handleSubmit}>
                            <div className="pt-4 pb-2 px-4">
                              <label className="block text-xs font-medium text-neutral-900 dark:text-white mb-1.5 sr-only">
                                Edit prompt
                              </label>
                              <Headless.Textarea
                                ref={textareaRef}
                                value={editedPrompt}
                                onFocus={() => triggerTextAreaResize()}
                                onChange={(e) => setEditedPrompt(e.currentTarget.value)}
                                className="w-full text-sm px-4 py-3 m-0 border-1 rounded-2xl border-neutral-200 dark:border-neutral-700 focus:border-neutral-300 dark:focus:border-white/20 ring-0 outline-none text-neutral-600 dark:text-neutral-400 focus:text-neutral-900 dark:focus:text-white focus:outline-none focus:ring-0 resize-none overflow-hidden transition-colors duration-150"
                                placeholder="Enter your prompt..."
                              />
                            </div>
                            <div className="flex items-center justify-end gap-2 pt-2 pb-4 px-4">
                              <Headless.Button
                                type="button"
                                onClick={() => setSelectedFollowUpOption(null)}
                                className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-white/10 rounded-md transition-colors"
                              >
                                Cancel
                              </Headless.Button>
                              <Headless.Button
                                type="submit"
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-700 rounded-md transition-colors"
                              >
                                Go
                                <PaperAirplaneIcon className="size-4" />
                              </Headless.Button>
                            </div>
                          </Form>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              <div className="flex flex-wrap items-center gap-2">
                {props.message.followUpOptions?.options?.map((option, index) => {
                  const isUsed = props.usedFollowUpIds?.has(option.id);
                  return (
                    <motion.button
                      key={option.id}
                      layout
                      initial={hasAnimatedButtons ? false : { opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        delay: hasAnimatedButtons ? 0 : index * 0.1,
                        duration: 0.2,
                        ease: 'anticipate'
                      }}
                      onClick={() => setSelectedFollowUpOption(option)}
                      className={clsx(
                        'inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-full border transition-colors duration-150',
                        isUsed
                          ? 'text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-500/20 border-blue-500 cursor-default'
                          : selectedFollowUpOption?.id === option.id
                            ? 'text-neutral-700 dark:text-neutral-300 bg-neutral-200 dark:bg-neutral-700 border-neutral-500 cursor-default'
                            : 'text-neutral-700 dark:text-neutral-300 bg-neutral-100 dark:bg-neutral-700 hover:bg-neutral-200 dark:hover:bg-white/10 border-neutral-200 dark:border-neutral-700'
                      )}
                      disabled={isUsed || selectedFollowUpOption?.id === option.id}
                    >
                      {isUsed || selectedFollowUpOption?.id === option.id ? (
                        <SolidSparklesIcon className="size-4" />
                      ) : (
                        <SparklesIcon className="size-4" />
                      )}
                      {option.title}
                      {isUsed ? (
                        <CheckIcon className="size-3" />
                      ) : (
                        <ArrowRightIcon className="size-3" />
                      )}
                    </motion.button>
                  );
                })}
              </div>
            </>
          )}
      </div>
    </AnimateIn>
  );
};
